"use client"
/**
 * Shared Project Requirements Drawer
 * Used by: /requirements/project-specific and /requirements/common
 * allowedType restricts which requirement type is shown and can be created.
 */
import React, { useState, useEffect, useCallback } from 'react'
import { useConfirm } from '../../provider/confirm.provider'
import { FaClipboardList, FaProjectDiagram } from 'react-icons/fa'
import { FiFileText, FiRefreshCw, FiX, FiCheck, FiPlus, FiAlertCircle } from 'react-icons/fi'
import axiosInstance from '../../libs/axios'
import { useAuth } from '../../provider/auth.provider'
import toast from 'react-hot-toast'

type RequirementType = 'project' | 'activity' | 'common'

interface Requirement {
  id: number
  title: string
  content: string
  requirement_type: RequirementType
  project_id: number | null
  activity_id: number | null
  is_active: boolean
  created_at: string
  updated_at: string
  project?: { id: number; project_name: string; project_short_name: string }
  activity?: { id: number; activity_name: string }
  creator?: { id: number; first_name: string; last_name: string }
  updater?: { id: number; first_name: string; last_name: string }
}

interface Project {
  id: number
  project_name: string
  project_short_name: string
  activity_id: number
  activity?: { id: number; activity_name: string }
}

const TYPE_LABELS: Record<RequirementType, string> = {
  project: 'Project Specific',
  activity: 'Activity Wise',
  common: 'Common (All)',
}

const TYPE_COLORS: Record<RequirementType, string> = {
  project: 'bg-blue-100 text-blue-800 border-blue-200',
  activity: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  common: 'bg-purple-100 text-purple-800 border-purple-200',
}

const TypeBadge = ({ type }: { type: RequirementType }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${TYPE_COLORS[type]}`}>
    {TYPE_LABELS[type]}
  </span>
)

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

export interface ProjectRequirementsDrawerProps {
  isOpen: boolean
  onClose: () => void
  project: Project | null
  /** Which requirement type to show and create. Other types are hidden. */
  allowedType: RequirementType
}

export const ProjectRequirementsDrawer: React.FC<ProjectRequirementsDrawerProps> = ({
  isOpen, onClose, project, allowedType
}) => {
  const { hasPermission, user } = useAuth()
  const { confirm } = useConfirm()
  const canCreate = hasPermission('OperationsRequirementsProjectActivity', allowedType === 'project' ? 'create_project_specific' : 'create_common_all')
  const canEdit = hasPermission('OperationsRequirementsProjectActivity', allowedType === 'project' ? 'edit_project_specific' : 'edit_common_all') && user?.role.id !== 7
  const canDelete = hasPermission('OperationsRequirementsProjectActivity', allowedType === 'project' ? 'delete_project_specific' : 'delete_common_all')

  const [requirements, setRequirements] = useState<Requirement[]>([])
  const [loading, setLoading] = useState(false)

  // Create form
  const [isCreating, setIsCreating] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newContent, setNewContent] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Edit state (inline)
  const [editingReq, setEditingReq] = useState<Requirement | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editContent, setEditContent] = useState('')
  const [editSubmitting, setEditSubmitting] = useState(false)

  const fetchRequirements = useCallback(async () => {
    if (!project) return
    try {
      setLoading(true)
      const params: any = { is_active: true }
      // For project-specific: filter by project_id
      // For common: filter by type=common
      if (allowedType === 'project') params.project_id = project.id
      if (allowedType === 'common') params.type = 'common'

      const res = await axiosInstance.get('/requirements', { params })
      const all: Requirement[] = res.data.data.requirements || []
      // Extra client-side filter for safety
      const filtered = all.filter(r => {
        if (allowedType === 'project') return r.requirement_type === 'project' && r.project_id === project.id
        if (allowedType === 'common') return r.requirement_type === 'common'
        return true
      })
      setRequirements(filtered)
    } catch { toast.error('Failed to load requirements') }
    finally { setLoading(false) }
  }, [project, allowedType])

  useEffect(() => {
    if (isOpen && project) {
      fetchRequirements()
      setIsCreating(false); setNewTitle(''); setNewContent('')
      setEditingReq(null)
    }
  }, [isOpen, project, fetchRequirements])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTitle.trim() || !newContent.trim()) { toast.error('Title and content required'); return }
    if (!project) return
    try {
      setSubmitting(true)
      const payload: any = { title: newTitle.trim(), content: newContent.trim(), requirement_type: allowedType }
      if (allowedType === 'project') payload.project_id = project.id
      await axiosInstance.post('/requirements', payload)
      toast.success('Requirement created')
      setIsCreating(false); setNewTitle(''); setNewContent('')
      fetchRequirements()
    } catch { } finally { setSubmitting(false) }
  }

  const startEdit = (req: Requirement) => {
    setEditingReq(req); setEditTitle(req.title); setEditContent(req.content)
    setIsCreating(false)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editTitle.trim() || !editContent.trim() || !editingReq) return
    try {
      setEditSubmitting(true)
      await axiosInstance.put(`/requirements/${editingReq.id}`, { title: editTitle.trim(), content: editContent.trim() })
      toast.success('Requirement updated')
      setEditingReq(null)
      fetchRequirements()
    } catch { } finally { setEditSubmitting(false) }
  }

  const handleDelete = async (req: Requirement) => {
    const ok = await confirm({
      title: "Delete requirement",
      message: `Delete "${req.title}"? This cannot be undone.`,
      confirmText: "Delete requirement",
      variant: "danger",
    })
    if (!ok) return
    try { await axiosInstance.delete(`/requirements/${req.id}`); fetchRequirements() } catch { }
  }

  if (!isOpen || !project) return null

  const typeColor = {
    project: 'text-blue-700',
    activity: 'text-emerald-700',
    common: 'text-purple-700',
  }[allowedType]

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <FaClipboardList className="text-orange-600" size={18} />
              {TYPE_LABELS[allowedType]} Requirements
            </h2>
            <p className="text-xs mt-0.5">
              <span className={`font-medium ${typeColor}`}>{project.project_name}</span>
              {project.activity && <span className="text-gray-400"> · {project.activity.activity_name}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canCreate && !isCreating && !editingReq && (
              <button onClick={() => setIsCreating(true)} className="px-4 bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm">
                <FiPlus size={13} /> Add
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg"><FiX size={18} className="text-gray-500" /></button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

          {/* Edit form */}
          {editingReq && (
            <form onSubmit={handleUpdate} className="bg-blue-50 border-2 border-blue-200 rounded p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h4 className="font-bold text-gray-900 text-sm">Edit — <TypeBadge type={editingReq.requirement_type} /></h4>
                <button type="button" onClick={() => setEditingReq(null)} className="p-1 hover:bg-blue-100 rounded"><FiX size={14} /></button>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Title *</label>
                <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Details *</label>
                <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={6} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 resize-y" />
              </div>
              {editingReq.creator && <p className="text-xs text-gray-400">By {editingReq.creator.first_name} {editingReq.creator.last_name} · Created: {formatDate(editingReq.created_at)} · Updated: {formatDate(editingReq.updated_at)}</p>}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditingReq(null)} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg">Cancel</button>
                <button type="submit" disabled={editSubmitting} className="px-4 py-1.5 text-xs font-medium text-white bg-blue-800 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                  {editSubmitting ? <FiRefreshCw size={12} className="animate-spin" /> : <FiCheck size={12} />} Update
                </button>
              </div>
            </form>
          )}

          {/* Create form */}
          {isCreating && !editingReq && (
            <form onSubmit={handleCreate} className=" border-2 border-black-200 p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h4 className="font-bold text-gray-900 text-sm">New — <TypeBadge type={allowedType} /></h4>
                <button type="button" onClick={() => setIsCreating(false)} className="p-1 hover:bg-orange-100 rounded"><FiX size={14} /></button>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Title *</label>
                <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Requirement title..." className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Details *</label>
                <textarea value={newContent} onChange={e => setNewContent(e.target.value)} placeholder="Describe the requirement..." rows={5} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500 resize-y" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setIsCreating(false)} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg">Cancel</button>
                <button type="submit" disabled={submitting} className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm">
                  {submitting ? <FiRefreshCw size={12} className="animate-spin" /> : <FiCheck size={12} />} Save
                </button>
              </div>
            </form>
          )}

          {/* Requirements list */}
          {loading ? (
            <div className="text-center py-10"><FiRefreshCw className="animate-spin mx-auto text-blue-800 mb-2" size={24} /><p className="text-sm text-gray-500">Loading...</p></div>
          ) : requirements.length === 0 && !isCreating && !editingReq ? (
            <div className="text-center py-10">
              <FiAlertCircle className="mx-auto text-gray-300 mb-2" size={36} />
              <p className="text-sm font-semibold text-gray-700">No {TYPE_LABELS[allowedType]} requirements</p>
              <p className="text-xs text-gray-400 mt-1">{canCreate ? 'Click "Add" to create one.' : 'None assigned.'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requirements.map(req => (
                <div
                  key={req.id}
                  className={`bg-white border rounded p-4 ${req.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'
                    }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h4 className="font-bold text-gray-900 text-sm">{req.title}</h4>
                        <TypeBadge type={req.requirement_type} />
                      </div>
                      {allowedType === 'project' && req.project && (
                        <div className="flex items-center gap-1 mb-1">
                          <FaProjectDiagram size={10} className="text-blue-600" />
                          <span className="text-xs text-blue-700 font-medium">{req.project.project_name}</span>
                        </div>
                      )}
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{req.content}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        {req.creator && <span>By {req.creator.first_name} {req.creator.last_name}</span>}
                        <span>Created: {formatDate(req.created_at)}</span>
                        <span>Updated: {formatDate(req.updated_at)}</span>
                        {req.updater && <span>· {req.updater.first_name} {req.updater.last_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {canEdit && <button onClick={() => startEdit(req)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-700" title="Edit"><FiFileText size={14} /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
